import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {
  AllowDepositApiArg,
  AllowDepositApiRes,
  BillingInformationComparisonResponseBodySchema,
  ChangeTypeOfSaleCheckoutOrderApiArg,
  ChangeTypeOfSaleCheckoutOrderApiResponse,
  SetDiscountForDisabledApiArg,
  SetDiscountForDisabledApiResponse,
  CreateBusinessCaseProformaInvoiceSettingApiArg,
  CreateBusinessCaseProformaInvoiceSettingApiResponse,
  CreateCheckoutOrderAdditionalCustomerApiArg,
  CreateCheckoutOrderAdditionalCustomerApiResponse,
  CreateDueDateSettingApiArg,
  CreateDueDateSettingApiResponse,
  CreateInternalCorrectiveTaxDocumentDocumentApiArg,
  CreateInternalCorrectiveTaxDocumentDocumentApiResponse,
  DeleteBusinessCaseProformaInvoiceSettingApiArg,
  DeleteBusinessCaseProformaInvoiceSettingApiResponse,
  DeleteCheckoutOrderAdditionalCustomerApiArg,
  DeleteCheckoutOrderAdditionalCustomerApiResponse,
  DeleteDueDateSettingApiArg,
  DeleteDueDateSettingApiResponse,
  DeletePaymentFileApiArg,
  DeletePaymentFileApiRes,
  ForbitDepositApiArg,
  ForbitDepositApiRes,
  GetAccountingOperationApiArg,
  GetAccountingOperationApiResponse,
  GetBusinessCaseProformaInvoiceSettingApiArg,
  GetBusinessCaseProformaInvoiceSettingApiResponse,
  GetCheckoutApiArg,
  GetCheckoutApiResponse,
  GetCheckoutOffsettingOfLiabilitiesAndReceivablesApiArg,
  GetCheckoutOffsettingOfLiabilitiesAndReceivablesApiResponse,
  GetCheckoutV2ApiArg,
  GetCheckoutV2ApiResponse,
  GetDueDateSettingApiArg,
  GetDueDateSettingApiResponse,
  GetListBusinessCaseProformaInvoiceSettingApiArg,
  GetListBusinessCaseProformaInvoiceSettingApiResponse,
  GetListDueDateSettingApiArg,
  GetListDueDateSettingApiResponse,
  GetOrderApiArg,
  GetOrderApiRes,
  GetSaleBillingInformationComparisonApiArg,
  GetSaleBillingInformationComparisonApiArgSchema,
  GetSaleBillingInformationComparisonApiResponse,
  MergeCheckoutOrderApiArg,
  MergeCheckoutOrderApiResponse,
  PatchCheckoutOrderAdditionalCustomerApiArg,
  PatchCheckoutOrderAdditionalCustomerApiResponse,
  PatchCheckoutOrderForeignCurrencyPaymentApiArg,
  PatchCheckoutOrderForeignCurrencyPaymentApiResponse,
  PatchSellerAsSupplierApiArg,
  PatchSellerAsSupplierApiResponse,
  PutCheckoutDocumentsApiArg,
  PutCheckoutDocumentsApiRes,
  RecordPaidCheckoutOrderPaymentApiArg,
  RecordPaidCheckoutOrderPaymentApiResponse,
  SplitCheckoutOrderApiArg,
  SplitCheckoutOrderApiResponse,
  UpdateBusinessCaseInvoiceSettingApiArg,
  UpdateBusinessCaseInvoiceSettingApiResponse,
  UpdateDueDateSettingApiArg,
  UpdateDueDateSettingApiResponse,
  WithdrawalPaymentApiArg,
  WithdrawalPaymentApiRes,
  WithdrawCheckoutOrderPaymentByCashRegisterDocumentApiArg,
  WithdrawCheckoutOrderPaymentByCashRegisterDocumentApiResponse,
} from './types';

export const checkoutApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    splitCheckoutOrder: build.mutation<SplitCheckoutOrderApiResponse, SplitCheckoutOrderApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/${queryArg.checkoutId}/order/${queryArg.orderId}/split`,
        method: 'PATCH',
        body: queryArg.orderSplitRequestBody,
      }),
      invalidatesTags: ['Checkout', 'offer'],
    }),
    getCheckout: build.query<GetCheckoutApiResponse, GetCheckoutApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/${queryArg.checkoutId}`,
      }),
      providesTags: (result, error, queryArg) => [{type: 'Checkout', id: queryArg.checkoutId}],
    }),
    getCheckoutV2: build.query<GetCheckoutV2ApiResponse, GetCheckoutV2ApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/checkout/${queryArg.checkoutId}`,
      }),
      providesTags: (result, error, queryArg) => [{type: 'Checkout', id: queryArg.checkoutId}],
    }),
    patchSellerAsSupplier: build.mutation<
      PatchSellerAsSupplierApiResponse,
      PatchSellerAsSupplierApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/${queryArg.checkoutId}/seller-as-supplier`,
        method: 'PATCH',
        body: queryArg.sellerAsSupplierRequestBody,
      }),
      // Switching on creates the brokerage purchase checkout when nobody has opened it yet. The
      // purchase business case is refetched, so the seller's bank accounts resolve through it.
      invalidatesTags: invalidateOnSuccess((_result, queryArg: PatchSellerAsSupplierApiArg) =>
        queryArg.sellerAsSupplierRequestBody.enabled
          ? ['Checkout', 'BusinessCaseDetail']
          : ['Checkout']
      ),
    }),
    mergeCheckoutOrder: build.mutation<MergeCheckoutOrderApiResponse, MergeCheckoutOrderApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/${queryArg.checkoutId}/order/${queryArg.orderId}/merge/${queryArg.targetOrderId}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Checkout', 'offer'],
    }),
    patchCheckoutOrderForeignCurrencyPayment: build.mutation<
      PatchCheckoutOrderForeignCurrencyPaymentApiResponse,
      PatchCheckoutOrderForeignCurrencyPaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/checkout/${queryArg.checkoutId}/order/${queryArg.orderId}/foreign-currency-payment`,
        method: 'PATCH',
        body: queryArg.orderForeignCurrencyPaymentRequestBody,
      }),
      invalidatesTags: (result, error, queryArgs) => [
        'Checkout',
        {type: 'offer', id: queryArgs.orderId},
      ],
    }),
    recordPaidCheckoutOrderPayment: build.mutation<
      RecordPaidCheckoutOrderPaymentApiResponse,
      RecordPaidCheckoutOrderPaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/${queryArg.checkoutId}/order/${queryArg.orderId}/payment/${queryArg.paymentId}/recordPaid`,
        method: 'POST',
        body: queryArg.orderPaymentRecordPaidRequestBody,
      }),
      invalidatesTags: (result, error, queryArgs) => [
        {type: 'offer', id: queryArgs.orderId},
        'Checkout',
        'invoiceProformaPaymentList',
        'BusinessCaseDetail',
        'BusinessCaseActions',
        'BusinessCaseActivity',
        'InterestActivity',
        'documentsCount',
      ],
    }),
    createCheckoutOrderAdditionalCustomer: build.mutation<
      CreateCheckoutOrderAdditionalCustomerApiResponse,
      CreateCheckoutOrderAdditionalCustomerApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/${queryArg.checkoutId}/order/${queryArg.orderId}/additional-customer`,
        method: 'POST',
        body: queryArg.createAdditionalCustomerRequestBody,
      }),
      invalidatesTags: ['Checkout', 'documentsCount'],
    }),
    deleteCheckoutOrderAdditionalCustomer: build.mutation<
      DeleteCheckoutOrderAdditionalCustomerApiResponse,
      DeleteCheckoutOrderAdditionalCustomerApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/${queryArg.checkoutId}/order/${queryArg.orderId}/additional-customer/${queryArg.additionalCustomerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Checkout'],
    }),
    patchCheckoutOrderAdditionalCustomer: build.mutation<
      PatchCheckoutOrderAdditionalCustomerApiResponse,
      PatchCheckoutOrderAdditionalCustomerApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/${queryArg.checkoutId}/order/${queryArg.orderId}/additional-customer/${queryArg.additionalCustomerId}`,
        method: 'PATCH',
        body: queryArg.patchAdditionalCustomerRequestBody,
      }),
      invalidatesTags: ['Checkout'],
    }),
    withdrawCheckoutOrderPaymentByCashRegisterDocument: build.mutation<
      WithdrawCheckoutOrderPaymentByCashRegisterDocumentApiResponse,
      WithdrawCheckoutOrderPaymentByCashRegisterDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/cash-register-document/${queryArg.cashRegisterDocumentId}/withdraw-payment`,
        method: 'PUT',
      }),
    }),

    allowDeposit: build.mutation<AllowDepositApiRes, AllowDepositApiArg>({
      query: ({checkoutId, orderId}) => ({
        url: `/dms/v1/checkout/${checkoutId}/order/${orderId}/allow-deposit`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'offer', id: queryArg.orderId}],
    }),
    forbidDeposit: build.mutation<ForbitDepositApiRes, ForbitDepositApiArg>({
      query: ({checkoutId, orderId}) => ({
        url: `/dms/v1/checkout/${checkoutId}/order/${orderId}/forbid-deposit`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'offer', id: queryArg.orderId}],
    }),
    getOrder: build.query<GetOrderApiRes, GetOrderApiArg>({
      query: ({checkoutId, orderId}) => ({
        url: `/dms/v1/checkout/${checkoutId}/order/${orderId}`,
      }),
      providesTags: (result, error, queryArg) => [{type: 'offer', id: queryArg.orderId}],
    }),
    getAccountingOperation: build.query<
      GetAccountingOperationApiResponse,
      GetAccountingOperationApiArg
    >({
      query: ({checkoutId, orderId, paymentId}) => ({
        url: `/dms/v1/checkout/${checkoutId}/order/${orderId}/payment/${paymentId}/accounting-operation`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'accountingOperation', id: queryArg.paymentId},
      ],
    }),
    withdrawalPayment: build.mutation<WithdrawalPaymentApiRes, WithdrawalPaymentApiArg>({
      query: ({checkoutId, orderId, paymentId, prescriptionId}) => ({
        url: `/dms/v1/checkout/${checkoutId}/order/${orderId}/payment/${paymentId}/withdraw/${prescriptionId}`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'Checkout',
        'invoicePayment',
        'invoiceProformaPaymentList',
        'BusinessCaseDetail',
        'BusinessCaseActions',
        'documentsCount',
        'paymentPrescription',
        'BusinessCaseActivity',
      ],
    }),
    deletePaymentFile: build.mutation<DeletePaymentFileApiRes, DeletePaymentFileApiArg>({
      query: ({checkoutId, orderId, paymentId, fileId}) => ({
        url: `/dms/v1/checkout/${checkoutId}/order/${orderId}/payment/${paymentId}/file/${fileId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'documentsCount',
      ],
    }),
    createBusinessCaseProformaInvoiceSetting: build.mutation<
      CreateBusinessCaseProformaInvoiceSettingApiResponse,
      CreateBusinessCaseProformaInvoiceSettingApiArg
    >({
      query: (body) => ({
        url: `/dms/v1/checkout/proforma-invoice-setting`,
        method: 'POST',
        body,
      }),
    }),
    deleteBusinessCaseProformaInvoiceSetting: build.mutation<
      DeleteBusinessCaseProformaInvoiceSettingApiResponse,
      DeleteBusinessCaseProformaInvoiceSettingApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/proforma-invoice-setting/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    getBusinessCaseProformaInvoiceSetting: build.query<
      GetBusinessCaseProformaInvoiceSettingApiResponse,
      GetBusinessCaseProformaInvoiceSettingApiArg
    >({
      query: ({id}) => ({
        url: `/dms/v1/checkout/proforma-invoice-setting/${id}`,
      }),
      keepUnusedDataFor: 0,
    }),
    updateBusinessCaseProformaInvoiceSetting: build.mutation<
      UpdateBusinessCaseInvoiceSettingApiResponse,
      UpdateBusinessCaseInvoiceSettingApiArg
    >({
      query: ({id, ...body}) => ({
        url: `/dms/v1/checkout/proforma-invoice-setting/${id}`,
        method: 'PUT',
        body,
      }),
    }),
    getListBusinessCaseProformaInvoiceSetting: build.query<
      GetListBusinessCaseProformaInvoiceSettingApiResponse,
      GetListBusinessCaseProformaInvoiceSettingApiArg
    >({
      query: () => ({
        url: `/dms/v1/checkout/proforma-invoice-setting/list`,
      }),
    }),
    changeTypeOfSaleCheckoutOrder: build.mutation<
      ChangeTypeOfSaleCheckoutOrderApiResponse,
      ChangeTypeOfSaleCheckoutOrderApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/${queryArg.checkoutId}/order/${queryArg.orderId}/type-of-sale`,
        method: 'PUT',
        body: queryArg.orderTypeOfSaleRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'Checkout',
        'BusinessCaseDetail',
      ],
    }),
    setDiscountForDisabled: build.mutation<
      SetDiscountForDisabledApiResponse,
      SetDiscountForDisabledApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/checkout/orders/${queryArg.orderId}/set-discount-for-disabled`,
        method: 'PUT',
        body: queryArg.setDiscountForDisabledRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'Checkout',
        'BusinessCaseDetail',
      ],
    }),
    createDocument: build.mutation<PutCheckoutDocumentsApiRes, PutCheckoutDocumentsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/document`,
        method: 'PUT',
        body: queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'documentsCount',
        'Checkout',
      ],
    }),
    getSaleBillingInformationComparison: build.query<
      GetSaleBillingInformationComparisonApiResponse,
      GetSaleBillingInformationComparisonApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/billing-information-comparison`,
      }),
      keepUnusedDataFor: 0,
      extraOptions: {
        requestSchema: GetSaleBillingInformationComparisonApiArgSchema,
        responseSchema: BillingInformationComparisonResponseBodySchema,
      },
    }),
    getCheckoutOffsettingOfLiabilitiesAndReceivables: build.query<
      GetCheckoutOffsettingOfLiabilitiesAndReceivablesApiResponse,
      GetCheckoutOffsettingOfLiabilitiesAndReceivablesApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/${queryArg.checkoutId}/offsetting-of-liabilities-and-receivables`,
      }),
    }),
    createInternalCorrectiveTaxDocumentDocument: build.mutation<
      CreateInternalCorrectiveTaxDocumentDocumentApiResponse,
      CreateInternalCorrectiveTaxDocumentDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/document/internal-corrective-tax-document`,
        method: 'PUT',
        body: queryArg.internalCorrectiveTaxDocumentRequestBody,
      }),
      invalidatesTags: (result, error, queryArgs) => [
        {type: 'offer', id: queryArgs.internalCorrectiveTaxDocumentRequestBody.orderId},
        'Checkout',
        'documentsCount',
      ],
    }),
    createDueDateSetting: build.mutation<
      CreateDueDateSettingApiResponse,
      CreateDueDateSettingApiArg
    >({
      query: (body) => ({
        url: `/dms/v1/checkout/due-date-setting`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['dueDates'],
    }),
    deleteDueDateSetting: build.mutation<
      DeleteDueDateSettingApiResponse,
      DeleteDueDateSettingApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/due-date-setting/${queryArg.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'dueDatesDetail', id: queryArg.id},
        'dueDates',
      ],
    }),
    getDueDateSetting: build.query<GetDueDateSettingApiResponse, GetDueDateSettingApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/checkout/due-date-setting/${queryArg.id}`,
      }),
      providesTags: (result, error, queryArg) => [{type: 'dueDatesDetail', id: queryArg.id}],
    }),
    updateDueDateSetting: build.mutation<
      UpdateDueDateSettingApiResponse,
      UpdateDueDateSettingApiArg
    >({
      query: ({id, ...body}) => ({
        url: `/dms/v1/checkout/due-date-setting/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'dueDatesDetail', id: queryArg.id},
        'dueDates',
      ],
    }),
    getListDueDateSetting: build.query<
      GetListDueDateSettingApiResponse,
      GetListDueDateSettingApiArg
    >({
      query: () => ({
        url: `/dms/v1/checkout/due-date-setting/list`,
      }),
      providesTags: ['dueDates'],
    }),
  }),
});

export const {
  useGetListDueDateSettingQuery,
  useUpdateDueDateSettingMutation,
  useGetDueDateSettingQuery,
  useDeleteDueDateSettingMutation,
  useCreateDueDateSettingMutation,
  useGetCheckoutOffsettingOfLiabilitiesAndReceivablesQuery,
  useCreateDocumentMutation,
  useChangeTypeOfSaleCheckoutOrderMutation,
  useGetBusinessCaseProformaInvoiceSettingQuery,
  useGetListBusinessCaseProformaInvoiceSettingQuery,
  useCreateBusinessCaseProformaInvoiceSettingMutation,
  useUpdateBusinessCaseProformaInvoiceSettingMutation,
  useDeleteBusinessCaseProformaInvoiceSettingMutation,
  useLazyGetOrderQuery,
  useDeletePaymentFileMutation,
  useGetOrderQuery,
  useWithdrawalPaymentMutation,
  useAllowDepositMutation,
  useForbidDepositMutation,
  useSplitCheckoutOrderMutation,
  useGetCheckoutQuery,
  useGetCheckoutV2Query,
  usePatchSellerAsSupplierMutation,
  useMergeCheckoutOrderMutation,
  useRecordPaidCheckoutOrderPaymentMutation,
  useCreateCheckoutOrderAdditionalCustomerMutation,
  usePatchCheckoutOrderAdditionalCustomerMutation,
  useDeleteCheckoutOrderAdditionalCustomerMutation,
  usePatchCheckoutOrderForeignCurrencyPaymentMutation,
  useCreateInternalCorrectiveTaxDocumentDocumentMutation,
  useSetDiscountForDisabledMutation,
  useLazyGetSaleBillingInformationComparisonQuery,
  useGetAccountingOperationQuery,
} = checkoutApi;
